import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAdminAuditLog, filterAdminAuditLog } from "api/auditApi";
import { unwrapList } from "api/unwrap";

/**
 * /audit-log — recent admin actions on this tenant. Compliance / security
 * review tool: "who deactivated this broker", "when did the audit schedule
 * change", etc.
 */

const ACTION_COLORS = {
  "broker.create": "#16a34a",
  "broker.update": "#0ea5e9",
  "broker.deactivate": "#dc2626",
  "broker.audit_schedule.save": "#a855f7",
  "broker.webhook.config": "#8b5cf6",
  "billing.checkout.completed": "#16a34a",
  "billing.subscription.upsert": "#0ea5e9",
  "billing.subscription.deleted": "#dc2626",
  "billing.payment.failed": "#dc2626",
  "driver.invite": "#16a34a",
  "driver.ready_to_drive": "#16a34a",
  "driver.rejected": "#dc2626",
  "driver.step_change": "#0ea5e9",
  "plan.cap.blocked": "#f97316",
  "billing.trial.will_end": "#f59e0b",
};

const QUICK_FILTERS = [
  { label: "Cap blocks", q: "plan.cap.blocked", color: "#f97316" },
  { label: "Billing", q: "billing.", color: "#16a34a" },
  { label: "Brokers", q: "broker.", color: "#0ea5e9" },
  { label: "Drivers", q: "driver.", color: "#a855f7" },
];

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

const prettyMetadata = (raw) => {
  if (!raw) return "";
  try {
    const obj = JSON.parse(raw);
    return Object.entries(obj)
      .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(" · ");
  } catch {
    return raw;
  }
};

export const AdminAuditLogPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [actionPrefix, setActionPrefix] = useState("");
  const [sinceDate, setSinceDate] = useState("");
  const [untilDate, setUntilDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const usingServer = !!(actionPrefix || sinceDate || untilDate);
    try {
      const res = usingServer
        ? await filterAdminAuditLog({
            actionPrefix: actionPrefix || undefined,
            since: sinceDate ? new Date(sinceDate).getTime() : undefined,
            until: untilDate ? new Date(untilDate).getTime() + 86399999 : undefined,
          })
        : await getAdminAuditLog();
      setRows(unwrapList(res));
    } catch (e) {
      // If the server-filter endpoint fails (e.g., regression in /audit-log/filter
      // like the one that took prod down on 2026-05-20), gracefully fall back to
      // the basic endpoint + client-side filtering. The page still shows data;
      // the filter inputs continue to refine via the existing client-side `filter`
      // text search. Surfaces an info-level warning rather than a blocking error.
      if (usingServer) {
        try {
          const res = await getAdminAuditLog();
          setRows(unwrapList(res));
          setError(
            "Server-side filter unavailable — showing latest 200 entries. " +
              "Use the free-text filter to narrow the result."
          );
        } catch (e2) {
          setError(e2?.message || e?.message || "Failed to load audit log");
        }
      } else {
        setError(e?.message || "Failed to load audit log");
      }
    } finally {
      setLoading(false);
    }
  }, [actionPrefix, sinceDate, untilDate]);

  const clearServerFilters = () => {
    setActionPrefix("");
    setSinceDate("");
    setUntilDate("");
  };
  const hasServerFilters = !!(actionPrefix || sinceDate || untilDate);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.action || ""} ${r.targetType || ""} ${r.metadata || ""} ${r.ipAddress || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, filter]);

  return (
    <Stack
      spacing={3}
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Admin Audit Log
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Privileged actions on this tenant — most recent 200. Use this to review who changed what when.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Filter by action / target / metadata / IP"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 320, maxWidth: 480 }}
        />
        <TextField
          size="small"
          placeholder="Action prefix (e.g. plan.cap.)"
          value={actionPrefix}
          onChange={(e) => setActionPrefix(e.target.value)}
          onBlur={load}
          sx={{ width: 200 }}
        />
        <TextField
          size="small"
          type="date"
          label="Since"
          InputLabelProps={{ shrink: true }}
          value={sinceDate}
          onChange={(e) => setSinceDate(e.target.value)}
          onBlur={load}
          sx={{ width: 160 }}
        />
        <TextField
          size="small"
          type="date"
          label="Until"
          InputLabelProps={{ shrink: true }}
          value={untilDate}
          onChange={(e) => setUntilDate(e.target.value)}
          onBlur={load}
          sx={{ width: 160 }}
        />
        {hasServerFilters ? (
          <Button size="small" variant="text" onClick={clearServerFilters} sx={{ minWidth: 0 }}>
            Clear filters
          </Button>
        ) : null}
        {QUICK_FILTERS.map((qf) => (
          <Chip
            key={qf.label}
            label={qf.label}
            size="small"
            onClick={() => setFilter(qf.q)}
            sx={{
              backgroundColor: filter === qf.q ? qf.color : "transparent",
              color: filter === qf.q ? "#fff" : qf.color,
              border: `1px solid ${qf.color}`,
              fontWeight: 600,
              cursor: "pointer",
            }}
          />
        ))}
        {filter ? (
          <Chip label="Clear" size="small" onClick={() => setFilter("")} sx={{ cursor: "pointer" }} />
        ) : null}
      </Box>

      {loading && rows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No matching audit entries.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.createdAtMs)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.action}
                      sx={{
                        backgroundColor: ACTION_COLORS[r.action] || "#64748b",
                        color: "#fff",
                        fontWeight: 600,
                        height: 22,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.targetType || "—"}
                      {r.targetId ? ` · ${r.targetId.slice(0, 8)}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {prettyMetadata(r.metadata)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.actorUserId ? r.actorUserId.slice(0, 8) : "system"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{r.ipAddress || "—"}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default AdminAuditLogPage;
